import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { ICatalogHeaderMap, ICatalogRow } from '../models/catalog';
import { ISkincareQuestion } from '../../questionario-skincare/models/skincare-question.interface';
import { buildQuestionHeaderAliases, buildTemplateHeaders } from '../constants';
import {
  escapeCatalogCsvValue,
  mapCatalogWeightToString,
  mapCatalogPriceToString,
  normalizeCatalogHeader,
  parseCatalogCsv,
  rowHasCatalogData,
  splitCatalogMultiValue
} from '../utils/catalogo-produtos.utils';

export interface ICatalogFileProcessingOptions {
  maxPreviewRows: number;
  maxPreviewColumns: number;
  headerAliases: Record<keyof ICatalogHeaderMap, string[]>;
  questions: ISkincareQuestion[];
}

export interface ICatalogFileProcessingResult {
  isSupported: boolean;
  previewHeaders: string[];
  previewColumns: string[];
  previewRows: string[][];
  previewTotalRows: number;
  previewMessage: string;
  rows: ICatalogRow[];
}

@Injectable({
  providedIn: 'root'
})
export class CatalogoProdutosImportService {
  async processSelectedFile(file: File, options: ICatalogFileProcessingOptions): Promise<ICatalogFileProcessingResult> {
    if (this.isCsvFile(file)) {
      const content = await this.readFileAsText(file);
      const preview = this.buildPreviewFromCsv(content, options.maxPreviewRows, options.maxPreviewColumns);
      const rows = this.buildRowsFromCsv(content, options.questions, options.headerAliases);
      return {
        ...preview,
        isSupported: true,
        rows
      };
    }

    if (this.isXlsxFile(file)) {
      const data = await this.readFileAsArrayBuffer(file);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];

      if (!sheetName) {
        return {
          isSupported: true,
          previewHeaders: [],
          previewColumns: [],
          previewRows: [],
          previewTotalRows: 0,
          previewMessage: 'Nao foi possivel ler a primeira aba do XLSX.',
          rows: []
        };
      }

      const sheet = workbook.Sheets[sheetName];
      const sheetRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as string[][];

      return {
        isSupported: true,
        previewHeaders: [],
        previewColumns: [],
        previewRows: [],
        previewTotalRows: 0,
        previewMessage: '',
        rows: this.buildRowsFromRows(sheetRows, options.questions, options.headerAliases)
      };
    }

    return {
      isSupported: false,
      previewHeaders: [],
      previewColumns: [],
      previewRows: [],
      previewTotalRows: 0,
      previewMessage: 'Formato nao suportado. Envie um arquivo CSV ou XLSX.',
      rows: []
    };
  }

  buildCsvFileFromRows(rows: ICatalogRow[], questions: ISkincareQuestion[]): File {
    const headers = buildTemplateHeaders(questions.length);
    const lines = [headers.join(',')];

    rows.forEach((row) => {
      if (!rowHasCatalogData(row)) {
        return;
      }

      const questionValues = questions.map((question) =>
        (row.answersByQuestionId?.[String(question.id)] || []).join(', ')
      );

      const values = [
        row.itemNumber,
        row.brandName,
        row.productName,
        row.productDescription,
        row.routineInformation,
        row.productPhotoLink,
        row.productLink,
        row.categories,
        row.productAddToCartCode,
        row.weight,
        mapCatalogPriceToString(row.price),
        row.acne,
        row.wrinkles,
        row.redness,
        row.eyebag,
        row.darkCircle,
        row.oiliness,
        ...questionValues
      ];

      lines.push(values.map((value) => escapeCatalogCsvValue(String(value ?? ''))).join(','));
    });

    const csvContent = lines.join('\n');
    return new File([new Blob([csvContent], { type: 'text/csv' })], 'catalogo.csv');
  }

  buildUploadFileForImport(
    importedRows: ICatalogRow[],
    file: File | null | undefined,
    questions: ISkincareQuestion[]
  ): File {
    if (importedRows.length > 0) {
      return this.buildCsvFileFromRows(importedRows, questions);
    }
    if (file) {
      return file;
    }
    return this.buildCsvFileFromRows([], questions);
  }

  private buildPreviewFromCsv(
    content: string,
    maxPreviewRows: number,
    maxPreviewColumns: number
  ): Omit<ICatalogFileProcessingResult, 'isSupported' | 'rows'> {
    const rows = parseCatalogCsv(content);

    if (!rows.length) {
      return {
        previewHeaders: [],
        previewColumns: [],
        previewRows: [],
        previewTotalRows: 0,
        previewMessage: 'Nao foi possivel gerar a pre-visualizacao do arquivo.'
      };
    }

    const previewHeaders = rows[0].map((header) => header.trim());
    const previewColumns = previewHeaders.slice(0, maxPreviewColumns);

    return {
      previewHeaders,
      previewColumns,
      previewRows: rows.slice(1, 1 + maxPreviewRows).map((row) => previewColumns.map((_, index) => row[index] ?? '')),
      previewTotalRows: Math.max(rows.length - 1, 0),
      previewMessage: ''
    };
  }

  private buildRowsFromCsv(
    content: string,
    questions: ISkincareQuestion[],
    headerAliases: Record<keyof ICatalogHeaderMap, string[]>
  ): ICatalogRow[] {
    const rows = parseCatalogCsv(content);
    return this.buildRowsFromRows(rows, questions, headerAliases);
  }

  private buildRowsFromRows(
    rows: string[][],
    questions: ISkincareQuestion[],
    headerAliases: Record<keyof ICatalogHeaderMap, string[]>
  ): ICatalogRow[] {
    if (rows.length <= 1) {
      return [];
    }

    const headers = rows[0].map((header) => header.trim());
    const headerIndex = new Map<string, number>();
    headers.forEach((header, index) => {
      const normalizedHeader = normalizeCatalogHeader(header);
      if (normalizedHeader) {
        headerIndex.set(normalizedHeader, index);
      }
    });

    const valueForRow = (row: string[], aliases: string[]): string => {
      for (const alias of aliases) {
        const index = headerIndex.get(normalizeCatalogHeader(alias));
        if (index !== undefined) {
          return row[index] ?? '';
        }
      }
      return '';
    };

    return rows
      .slice(1)
      .filter((row) => row.some((cell) => String(cell ?? '').trim().length > 0))
      .map((row) => {
        const valueFor = (aliases: string[]): string => valueForRow(row, aliases);

        const answersByQuestionId: { [questionId: string]: string[] } = {};
        questions.forEach((question, index) => {
          const rawValue = valueFor(buildQuestionHeaderAliases(index));
          answersByQuestionId[String(question.id)] = splitCatalogMultiValue(rawValue);
        });

        return {
          itemNumber: valueFor(headerAliases.itemNumber),
          brandName: valueFor(headerAliases.brandName),
          productName: valueFor(headerAliases.productName),
          productDescription: valueFor(headerAliases.productDescription),
          routineInformation: valueFor(headerAliases.routineInformation),
          productPhotoLink: valueFor(headerAliases.productPhotoLink),
          productLink: valueFor(headerAliases.productLink),
          categories: valueFor(headerAliases.categories),
          productAddToCartCode: valueFor(headerAliases.productAddToCartCode),
          weight: mapCatalogWeightToString(valueFor(headerAliases.weight)),
          price: mapCatalogPriceToString(valueFor(headerAliases.price)),
          acne: valueFor(headerAliases.acne),
          wrinkles: valueFor(headerAliases.wrinkles),
          redness: valueFor(headerAliases.redness),
          eyebag: valueFor(headerAliases.eyebag),
          darkCircle: valueFor(headerAliases.darkCircle),
          oiliness: valueFor(headerAliases.oiliness),
          answersByQuestionId
        };
      })
      .filter((row) => rowHasCatalogData(row));
  }

  private isCsvFile(file: File): boolean {
    return file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
  }

  private isXlsxFile(file: File): boolean {
    return file.name.toLowerCase().endsWith('.xlsx');
  }

  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(String(reader.result || ''));
      reader.onerror = (): void => reject(new Error('Nao foi possivel ler o arquivo.'));
      reader.readAsText(file);
    });
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (): void => resolve(reader.result as ArrayBuffer);
      reader.onerror = (): void => reject(new Error('Nao foi possivel ler o arquivo.'));
      reader.readAsArrayBuffer(file);
    });
  }
}
